import React, { useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Box, Grid, Stack, Typography } from "@mui/material";
import ReactCountryFlag from "react-country-flag";
import LayerdIcon from "../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";
import { ReactComponent as BackIcon } from "../../../../../vendor/_new/icon/detections/back.svg";

import { DetectionFilterCategory } from "../../../../../utils/constants";
import colors from "../../../../../utils/colors";
import { PanelSkeleton, DashboardPanelEmptyState } from "./Dashboard";
import useSite from "../../../../../hooks/user/useSite";
import { displayUserAgent } from "../../../../../utils/display";

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

const safeRegionName = (countryCode) => {
  const code = (countryCode || "").toString().trim().toUpperCase();
  if (!code) return "—";
  try {
    return regionNames.of(code) || code;
  } catch {
    return code;
  }
};

const WebApplicationDetectionList = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();

  const {
    top_region_detection,
    top_source_detection,
    top_path,
    top_ua,
    top_detection_type,
    top_method,
    getTopUA,
    getTopDetectionType,
    getTopMethod,
    getTopPath,
    getTopSource,
    getTopRegion,
  } = useSite();

  const tmpData = JSON.parse(decodeURIComponent(searchParams.get("data")));
  const siteID = tmpData?.siteID || "all";
  const timeRange = tmpData?.timeRange || "7d";

  useEffect(() => {
    switch (categoryId) {
      case DetectionFilterCategory.TopWAFDetectionsByCountry:
        getTopRegion(siteID, timeRange, 20, null);
        break;
      case DetectionFilterCategory.TopUserAgent:
        getTopUA(siteID, timeRange, 20, null);
        break;
      case DetectionFilterCategory.TopPath:
        getTopPath(siteID, timeRange, 20, null);
        break;
      case DetectionFilterCategory.TopEventBySource:
        getTopSource(siteID, timeRange, 20, null);
        break;
      case DetectionFilterCategory.TopMethod:
        getTopMethod(siteID, timeRange, 20, null);
        break;
      case DetectionFilterCategory.TopDetection:
        getTopDetectionType(siteID, timeRange, 20, null);
        break;
      default:
        break;
    } // eslint-disable-next-line
  }, [categoryId, getTopRegion, getTopUA, getTopPath, getTopSource, getTopMethod, getTopDetectionType]);

  const [subTitle, key] = useMemo(() => {
    switch (categoryId) {
      case DetectionFilterCategory.TopWAFDetectionsByCountry:
        return ["Top WAF detections by country", "country_iso_code"];
      case DetectionFilterCategory.TopUserAgent:
        return ["Top user agent", "ua"];
      case DetectionFilterCategory.TopPath:
        return ["Top paths", "path"];
      case DetectionFilterCategory.TopEventBySource:
        return ["Top events by source", "addr"];
      case DetectionFilterCategory.TopMethod:
        return ["Top methods", "method"];
      case DetectionFilterCategory.TopDetection:
        return ["Top detections", "attack_type"];
      default:
        return ["Top WAF detections by country", "country_iso_code"];
    }
  }, [categoryId]);

  const detectionList = useMemo(() => {
    switch (categoryId) {
      case DetectionFilterCategory.TopWAFDetectionsByCountry:
        return top_region_detection;
      case DetectionFilterCategory.TopUserAgent:
        return top_ua;
      case DetectionFilterCategory.TopPath:
        return top_path;
      case DetectionFilterCategory.TopEventBySource:
        return top_source_detection;
      case DetectionFilterCategory.TopMethod:
        return top_method;
      case DetectionFilterCategory.TopDetection:
        return top_detection_type;
      default:
        return [];
    }
  }, [categoryId, top_region_detection, top_ua, top_path, top_source_detection, top_method, top_detection_type]);

  const handleDetail = async (categoryId, data) => {
    navigate(
      `/v2/detection/web_application/dashboard/${categoryId}/detail?data=${encodeURIComponent(JSON.stringify({ siteID, timeRange, data }))}`
    );
  };

  return (
    <>
      <Helmet title="WAF Dashboard" />
      <Stack spacing={6} sx={{ p: 6 }}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          onClick={() => navigate("/v2/detection/web_application/dashboard")}
          sx={{ cursor: "pointer" }}
        >
          <BackIcon style={{ color: colors.primary[300] }} />
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
            WAF Dashboard
          </Typography>
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[800] }}>
            /
          </Typography>
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[800] }}>
            {subTitle}
          </Typography>
        </Stack>
        <Box sx={{ width: "100%", border: `solid 1px ${colors.neutral[200]}`, p: 2, borderRadius: "8px" }}>
          <Grid container>
            {detectionList === null && <PanelSkeleton />}
            {detectionList !== null && detectionList.length === 0 && <DashboardPanelEmptyState />}
            {detectionList?.map((item, index) => (
              <Grid key={`waf_event_${index}`} item xs={12}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  spacing={3}
                  onClick={() => handleDetail(categoryId, item?.[key])}
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    height: 42,
                    "&:hover": { backgroundColor: colors.primary["alpha"] },
                    cursor: "pointer",
                  }}
                >
                  <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                    {key === "country_iso_code" && (
                      <ReactCountryFlag
                        countryCode={item?.[key]}
                        svg
                        style={{
                          width: "26px",
                          height: "18px",
                        }}
                      />
                    )}
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}>
                      {key === "country_iso_code"
                        ? safeRegionName(item?.[key])
                        : key === "ua"
                        ? displayUserAgent(item?.[key])
                        : item?.[key]}
                    </Typography>
                  </Stack>
                  <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                    <LayerdIcon icon={DETECTION_ICONS.detection} size={18} color="neutral" />
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[600], pr: 3 }}>
                      {item.count}
                    </Typography>
                    <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                  </Stack>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>
    </>
  );
};
export default WebApplicationDetectionList;
